import React, { useCallback, useEffect, useState } from "react";
import { portalApi, PortalBillingPayment, PortalBillingSubscription } from "../../api/portalApi";

function formatMoney(cents: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency.toUpperCase() === "USD" ? "USD" : currency,
  }).format(cents / 100);
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) {
    return "—";
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return "—";
  }
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

const PortalBillingSection: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [subscription, setSubscription] = useState<PortalBillingSubscription | null>(null);
  const [payments, setPayments] = useState<PortalBillingPayment[]>([]);

  const load = useCallback(async () => {
    setError("");
    setLoading(true);
    try {
      const [sub, pays] = await Promise.all([portalApi.getBillingSubscription(), portalApi.getBillingPayments()]);
      setSubscription(sub);
      setPayments(pays);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load billing.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <h2 className="text-xs font-semibold uppercase tracking-[0.05em] text-slate-600">Billing</h2>
      <p className="mt-1 text-sm text-slate-600">Your plan and recent payments.</p>

      {loading && <p className="mt-4 text-sm text-slate-500">Loading…</p>}
      {error && (
        <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800" role="alert">
          {error}
        </p>
      )}

      {!loading && !error && (
        <div className="mt-4 space-y-4">
          {subscription ? (
            <div className="rounded-lg border border-slate-100 bg-slate-50 p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Current plan</p>
              <p className="mt-1 text-lg font-semibold text-navy-600">{subscription.planName || "Plan"}</p>
              <p className="mt-1 text-sm text-slate-600">
                {formatMoney(subscription.amountCents, subscription.currency)} · {subscription.status}
              </p>
              {subscription.currentPeriodEnd && (
                <p className="mt-2 text-xs text-slate-500">
                  Current period ends {formatDate(subscription.currentPeriodEnd)}
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-slate-600">No active subscription is on file. Contact your advisor if you have questions.</p>
          )}

          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Recent payments</p>
            {payments.length === 0 ? (
              <p className="mt-2 text-sm text-slate-600">No payments to show yet.</p>
            ) : (
              <ul className="mt-2 divide-y divide-slate-100">
                {payments.map((p) => (
                  <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                    <span className="font-medium text-slate-800">{formatMoney(p.amountCents, p.currency)}</span>
                    <span className="text-slate-500">{formatDate(p.paidAt)}</span>
                    <span className="text-xs text-slate-400">{p.status}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default PortalBillingSection;
